import parseEnterChat from '@module/helpers/chat/parseEnterChat';
import { useAdChatConfigQuery, useChatMessageMutation, useSaveAdChatMutation } from '@module/queries/chat/chatQueryHooks';
import type { FrozenMessage } from '@module/types/centrifuge/public-message/freezeMessageTypes';
import type { FanLevel } from '@module/types/centrifuge/publicMessageTypes';
import checkIsAdChat from '@module/utils/checkIsAdChat';
import toHalfLetter from '@module/utils/toHalfLetter';
import dayjs from 'dayjs';
import useChatMessageStore from '../../stores/useChatMessageStore';
import useChatPanelStore from '../../stores/useChatPanelStore';
import { useFetchLivePlayMutationStore } from '../../stores/useFetchLivePlayMutationStore';
import useSocketStore from '../../stores/useSocketStore';
import useGetLoginInfoQuery from '../auth/useGetLoginInfoQuery';
import buildSuspiciousAdChatLog from './buildSuspiciousAdChatLog';
import { createFakeChatMessage } from './createFakeChatMessage';
import resolveFreezeChatPermission from './resolveFreezeChatPermission';
import useChatErrorHandler from './useChatErrorHandler';
import useChatInputMeta from './useChatInputMeta';
interface Props {
  chatInputValue: string; // 내재되어 있으면, 열혈팬 탭에 갔다 왔을 시 적어둔 채팅 input value가 초기화되는 문제가 있음
  setChatInputValue: React.Dispatch<React.SetStateAction<string>>;
  onFreezeOnError?: (message: string) => void;
  messageOnPaperingOnError?: string;
  messageOnUnexpectedChatError?: string;
  messageOnSubmitChatOnPaperingBlockTime?: string;
  messageOnSubmitChatOnMuteTime?: string;
  messageOnSubmitChatOnFrozen?: string;
}
const useChatComposer = ({
  chatInputValue,
  setChatInputValue,
  onFreezeOnError,
  messageOnPaperingOnError = '지속적인 채팅으로인해 30초간 채팅을 금지합니다.',
  messageOnUnexpectedChatError = '채팅이 원활하지 않습니다. 잠시 후 다시 시도해주세요.',
  messageOnSubmitChatOnPaperingBlockTime = '도배로 인해 채팅금지 상태입니다.',
  messageOnSubmitChatOnMuteTime = '채팅금지 상태입니다.',
  messageOnSubmitChatOnFrozen = '채팅창이 잠겨 있습니다.'
}: Props) => {
  const {
    userInfo,
    isLogin
  } = useGetLoginInfoQuery();
  const {
    token,
    payloadInfo
  } = useSocketStore();
  const setChatMessages = useChatMessageStore(state => state.setChatMessages);
  const {
    data: playInfo
  } = useFetchLivePlayMutationStore();
  const {
    closeChatInputArea,
    setIsChatEmoticonSelectBoxOpen,
    selectedBlockEmoticon,
    clearSelectedBlockEmoticon,
    setIsChatNudgeSent
  } = useChatPanelStore(state_0 => ({
    closeChatInputArea: state_0.closeChatInputArea,
    setIsChatEmoticonSelectBoxOpen: state_0.setIsChatEmoticonSelectBoxOpen,
    selectedBlockEmoticon: state_0.selectedBlockEmoticon,
    clearSelectedBlockEmoticon: state_0.clearSelectedBlockEmoticon,
    setIsChatNudgeSent: state_0.setIsChatNudgeSent
  }));
  const {
    appendChatErrorMessage,
    handleChatMessageError,
    paperingBlockTime,
    muteTime
  } = useChatErrorHandler({
    onFreezeOnError,
    messageOnPaperingOnError,
    messageOnUnexpectedChatError
  });
  const {
    chatInputRef,
    roomEnterTime,
    typingStart,
    lastKeyPressed,
    markTypingStart,
    trackKeyPress
  } = useChatInputMeta();
  const bjIdx = playInfo?.media.userIdx || 0;
  const myIdx = userInfo?.idx;
  const {
    data: adChatData
  } = useAdChatConfigQuery({
    type: 'ad'
  });
  const {
    mutate: saveAdChat
  } = useSaveAdChatMutation();
  const {
    mutate: postChatMessage
  } = useChatMessageMutation({
    onSuccess: () => {
      setIsChatNudgeSent(true);
    },
    onError: handleChatMessageError
  });
  const handleSubmitChat = async (intentionalMessage?: string) => {
    if (!playInfo || !token) return;
    const messageToSend = intentionalMessage || chatInputValue;

    // 로그인/본인인증 게이팅은 호출처 책임(전송 진입점에서 처리). 여기선 순수 전송 로직만.

    // 채팅 내용 초기화
    setChatInputValue('');

    // 내용 없이 채팅 전송 시도하면 채팅창 닫기
    if (messageToSend.trim() === '' && !selectedBlockEmoticon) {
      closeChatInputArea();
      return;
    }
    const now = Date.now();

    // 도배로 인한 채팅 금지 상태인 경우
    if (now < paperingBlockTime) {
      appendChatErrorMessage(messageOnSubmitChatOnPaperingBlockTime);
      return;
    }

    // 채팅금지 상태인 경우
    if (now < muteTime) {
      appendChatErrorMessage(messageOnSubmitChatOnMuteTime);
      return;
    }

    // ---------freeze 상태에서 채팅 허용 여부 체크---------
    const fanInfo = playInfo.fan;
    // jwt payload 에서 최신 유저정보 파싱
    const tokenInfo = payloadInfo(token);
    if (tokenInfo) {
      fanInfo.level = tokenInfo.info?.lev ?? 0;
      fanInfo.rank = tokenInfo.info?.rk ?? 0;
      fanInfo.isManager = tokenInfo.info.type === 'manager';
    }
    if (!resolveFreezeChatPermission(playInfo.roomFreezeInfo, fanInfo)) {
      setChatMessages(prev => {
        const newFreezePayload: FrozenMessage = {
          data: {
            type: 'Frozen',
            message: messageOnSubmitChatOnFrozen
          },
          offset: now
        };
        return [...prev, newFreezePayload];
      });
      return;
    }

    // ---------광고성 채팅 체크---------
    const replacedMessage = toHalfLetter(messageToSend).replace(/[^a-zA-Z0-9가-힣ㄱ-ㅎㅏ-ㅣ]/g, '').toUpperCase();
    const isAdChat = adChatData ? checkIsAdChat(adChatData, replacedMessage) : false;

    // 광고성 의심 유저 채팅 정보 서버에 저장
    const suspiciousAdChatLog = buildSuspiciousAdChatLog({
      now: dayjs(),
      message: messageToSend,
      replacedChat: replacedMessage,
      isAdChat,
      adConfig: adChatData?.adConfig,
      user: userInfo,
      fan: playInfo.fan,
      bjIdx,
      myIdx,
      mediaCode: playInfo.media.code || '',
      roomEnterTime: roomEnterTime.current,
      lastKeyPressed,
      typingStart
    });
    if (suspiciousAdChatLog) saveAdChat(suspiciousAdChatLog);

    // 광고성 채팅일 경우 실제 전송하지 않고 가짜 채팅 메시지 추가
    if (isAdChat) {
      const fakeChatMessage = createFakeChatMessage({
        isManager: playInfo.fan.isManager,
        isRoomMaster: playInfo.fan.isRoomMaster,
        fanLevel: playInfo.fan.level as FanLevel,
        fanRank: playInfo.fan.rank || 0,
        userId: userInfo?.id || '',
        userGender: userInfo?.gender === 'M' ? 'M' : 'F',
        userIdx: userInfo?.idx || 0,
        userNick: userInfo?.nick || '',
        message: messageToSend,
        blockEmoticon: selectedBlockEmoticon
      });
      setChatMessages(prev_0 => [...prev_0, fakeChatMessage]);
      // 전송(광고성 가짜 포함) 후 이모티콘 선택 박스 + 미리보기 모두 닫기
      clearSelectedBlockEmoticon();
      setIsChatEmoticonSelectBoxOpen(false);
      return;
    }

    // ---------채팅 전송---------
    postChatMessage({
      message: messageToSend,
      ...(selectedBlockEmoticon && {
        emoticonIdx: selectedBlockEmoticon.idx
      }),
      token,
      roomid: playInfo.media.code || '',
      channel: playInfo.media.userIdx,
      t: playInfo.chatServer.t,
      chatToken: playInfo.chatServer.token
    });

    // 전송 후 이모티콘 선택 박스 + 미리보기 모두 닫기
    clearSelectedBlockEmoticon();
    setIsChatEmoticonSelectBoxOpen(false);
  };
  const handleChatInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!isLogin) return;
    if ((event as any)?.isComposing || event?.keyCode === 229) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      void handleSubmitChat();
      return;
    }
    trackKeyPress(event);
  };

  // 채팅방 진입/퇴장 라이프사이클은 별개 관심사이므로 각 소비처에서 useChatRoomLifecycle로 직접 호출한다.
  const enterChat = playInfo ? parseEnterChat(playInfo) : null;
  return {
    chatInputRef,
    handleChatInputKeyDown,
    // 입력 시작 시각 기록(텔레메트리). 이모티콘 박스 닫기 등 입력 클릭 UI 정책은 각 소비처가 소유한다.
    markTypingStart,
    handleSubmitChat,
    enterChat
  };
};
export default useChatComposer;